import { useQuery } from "@tanstack/react-query";
import { Bell, CircleDashed, Clock, type LucideIcon } from "lucide-react";
import { Link } from "wouter";

import { PILL_STAGGER_MS } from "@/components/agent-triggers/pillStagger.ts";

import { cn } from "@/lib/utils.ts";
import { userTaskHref } from "@/types/userTaskLinks.ts";
import { useTriggerTaskFlash } from "@/shared/userTaskFlash.ts";
import { userTaskQueries } from "./api.ts";
import {
  chipAccessibleName,
  type UserTaskChip,
  type UserTaskChipTone,
  pickUserTaskChips,
} from "@/shared/userTaskChips.ts";
import { useTaskGates } from "./useTaskGates.ts";

/**
 * What each tone is worth a glance for, as a mark and a colour.
 *
 * The three read as a progression, and all three speak about time, which is
 * the only axis they differ on: an open ring for a task with no date on it
 * yet, a clock once there is a time to meet, a bell once that time has
 * passed. No tick anywhere -- these are tasks the user still owes, and a tick
 * reads as done.
 *
 * iOS draws that open ring dotted; lucide's is dashed, the nearest it has.
 *
 * The word itself is not drawn: the title is what the user scans for, and the
 * tone still reaches a screen reader through the link's label, which colour
 * alone could never do.
 */
const TONE: Record<UserTaskChipTone, { Icon: LucideIcon; className: string }> = {
  overdue: { Icon: Bell, className: "text-destructive" },
  new: { Icon: CircleDashed, className: "text-primary" },
  due: { Icon: Clock, className: "text-zinc-700" },
};

/**
 * The user's own tasks worth a glance, or none without `tasks-v2`.
 *
 * A hook rather than something the row works out for itself, because the page
 * that draws it has to know how many there are: tasks and prompt suggestions
 * share a fixed number of slots, and whatever the tasks do not take is what the
 * suggestions get. *
 * Reports whether the list has settled as well as what it holds. The
 * suggestions fill whatever slots the tasks leave, so drawing them against an
 * empty task list hands them the whole budget and then takes it back a moment
 * later — pills rise into place and vanish on every cold open, which is the
 * ordinary way this screen is reached.
 */
export const useUserTaskChips = (maxTasks: number): { chips: UserTaskChip[]; settled: boolean } => {
  const { list: enabled } = useTaskGates();
  const { data, isPending } = useQuery({ ...userTaskQueries.list(), enabled });
  if (!enabled) return { chips: [], settled: true };
  return { chips: pickUserTaskChips(data?.tasks ?? [], maxTasks), settled: !isPending };
};

/**
 * The user's own tasks on the new chat page, stacked above the prompt
 * suggestions and wearing their shape exactly: same height, same text size,
 * same icon box. The two kinds share one column, so a difference in either
 * would read as a mistake rather than a distinction — what tells them apart is
 * the mark and its colour.
 *
 * No heading over them. One would label the first rows of a stack and not the
 * rest, and the stack is the point.
 *
 * Renders nothing when there are none to show.
 */
export const UserTaskChips = ({ chips }: { chips: UserTaskChip[] }) => {
  const triggerFlash = useTriggerTaskFlash();
  if (chips.length === 0) return null;

  return (
    <ul className="flex w-full flex-col items-start gap-2">
      {chips.map((chip, index) => {
        const { task, tone } = chip;
        const { Icon, className } = TONE[tone];
        return (
          <li
            key={task.id}
            className="max-w-full animate-enter"
            style={{ animationDelay: `${index * PILL_STAGGER_MS}ms`, animationFillMode: "both" }}
          >
            <Link
              href={userTaskHref(task.id)}
              className={cn(
                "flex max-w-full items-center gap-3 rounded-3xl border bg-card py-2.5 pr-5 pl-4",
                "text-left text-sm text-foreground shadow-xs",
                "transition-colors duration-200 hover:bg-secondary",
                "focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
              )}
              data-statsig-name="new-chat-tasks-chip"
              aria-label={chipAccessibleName(chip)}
              // The glow is this chip's doing, so this is what asks for it.
              // Keyed on the route instead, it would fire again every time the
              // user refreshed or came back from another filter.
              onClick={() => triggerFlash(task.id)}
            >
              {/* The same 20px box a suggestion's emoji or logo fills, so both
                  kinds of pill start their text on the same vertical line. */}
              <span aria-hidden="true" className="flex size-5 shrink-0 items-center justify-center">
                <Icon className={cn("size-4", className)} />
              </span>
              <span className="line-clamp-2">{task.title}</span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
};
